/**
 * AiFixerPanelComponent — generic AI fixer panel (state machine).
 *
 * This component is task-type-agnostic. It:
 * - Accepts a fully-formed job request from the caller
 * - Manages the IDLE → RUNNING → COMPLETED/FAILED lifecycle
 * - Projects a domain-specific result view via <ng-content select="[ai-result]">
 * - Emits (accepted) / (rejected) events for the caller to handle
 *
 * Usage:
 *   <ai-fixer-panel [jobRequest]="myRequest" [firebaseToken]="token"
 *                    (accepted)="onAccept($event)" (rejected)="onReject()">
 *       <my-domain-result ai-result [data]="..."></my-domain-result>
 *   </ai-fixer-panel>
 */
import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AiAgentJobRequest } from '../../interfaces/ai-agent-job-request.interface';
import { AiAgentStatus } from '../../interfaces/ai-agent-status.enum';
import { AiBackendError } from '../../interfaces/ai-backend-error';
import { AiAgentProvider } from '../../provider/ai-agent.provider';

export type AiFixerPanelState = 'IDLE' | 'RUNNING' | 'COMPLETED' | 'FAILED';

@Component({
    selector: 'ai-fixer-panel',
    templateUrl: './ai-fixer-panel.component.html',
    styleUrls: ['./ai-fixer-panel.component.scss'],
})
export class AiFixerPanelComponent implements OnInit, OnDestroy {
    /** The fully-formed job request — built by the domain adapter */
    @Input() jobRequest: AiAgentJobRequest | null = null;

    /** Firebase ID token — used to open the WebSocket connection */
    @Input() firebaseToken: string = '';

    /** Emits the raw session output when the AI job reaches COMPLETED state (before accept/reject) */
    @Output() completed = new EventEmitter<Record<string, unknown>>();

    /** Emits the raw session output when the user clicks "Accept" */
    @Output() accepted = new EventEmitter<Record<string, unknown>>();

    /** Emits when user clicks "Reject" / "Cancel" */
    @Output() rejected = new EventEmitter<void>();

    /** Internal state machine */
    panelState: AiFixerPanelState = 'IDLE';

    /** Latest session output (null until COMPLETED) */
    sessionOutput: Record<string, unknown> | null = null;

    /** Typed backend error when the panel is in FAILED state. */
    error: AiBackendError | null = null;

    private readonly destroy$ = new Subject<void>();

    constructor(public readonly aiProvider: AiAgentProvider) {}

    ngOnInit(): void {
        // Track session status → update panelState
        this.aiProvider.status$
            .pipe(takeUntil(this.destroy$))
            .subscribe((status) => {
                if (!status) {
                    // Session was reset externally (e.g. user removed the uploaded file
                    // mid-job). Return the panel to IDLE so the user can start over —
                    // without this branch the panel would remain stuck on RUNNING/
                    // COMPLETED/FAILED forever.
                    this.panelState = 'IDLE';
                    this.sessionOutput = null;
                    this.error = null;
                    return;
                }

                switch (status) {
                    case AiAgentStatus.PENDING:
                    case AiAgentStatus.RUNNING:
                        this.panelState = 'RUNNING';
                        break;
                    case AiAgentStatus.COMPLETED:
                    case AiAgentStatus.COMPLETED_WITH_WARNINGS:
                        this.panelState = 'COMPLETED';
                        // Emit the output immediately so callers can preview before accepting
                        if (this.sessionOutput) {
                            this.completed.emit(this.sessionOutput);
                        }
                        break;
                    case AiAgentStatus.FAILED:
                        this.panelState = 'FAILED';
                        break;
                }
            });

        // Track output
        this.aiProvider.output$
            .pipe(takeUntil(this.destroy$))
            .subscribe((output) => {
                this.sessionOutput = output;
                // Emit completed here too — handles the case where output$ fires after status$
                if (output && this.panelState === 'COMPLETED') {
                    this.completed.emit(output);
                }
            });

        // Track errors — single typed value object
        this.aiProvider.error$
            .pipe(takeUntil(this.destroy$))
            .subscribe((error) => {
                this.error = error;
            });
    }

    /** Start the AI job */
    startJob(): void {
        if (!this.jobRequest) return;

        this.panelState = 'RUNNING';
        this.error = null;

        // Connect WebSocket if token provided
        if (this.firebaseToken) {
            this.aiProvider.connectSocket(this.firebaseToken);
        }

        // Submit the job
        this.aiProvider.submitJob(this.jobRequest);
    }

    /** Called from the projected result component or panel buttons */
    acceptResult(): void {
        if (this.sessionOutput) {
            this.accepted.emit(this.sessionOutput);
        }
        this.aiProvider.resetSession();
    }

    /** Called from the projected result component or panel buttons */
    rejectResult(): void {
        this.rejected.emit();
        this.aiProvider.resetSession();
    }

    /** Retry after failure */
    retry(): void {
        this.panelState = 'IDLE';
        this.error = null;
        this.aiProvider.resetSession();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.aiProvider.disconnectSocket();
    }
}
